import { FC, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Check, Copy, X } from 'lucide-react';
import { PageCard, SectionLabel, TOKENS } from '../../../components/design-system';
import { createStock } from '../api';

interface Props {
  manufacturer_id: number;
  open: boolean;
  onClose: () => void;
}

export const CreateStockModal: FC<Props> = ({ manufacturer_id, open, onClose }) => {
  const qc = useQueryClient();
  const [bluetooth, setBluetooth] = useState('');
  const [cmbNumber, setCmbNumber] = useState('');
  const [intended, setIntended] = useState('');
  const [notes, setNotes] = useState('');
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation(
    () => createStock({
      manufacturer_id,
      bluetooth: bluetooth.trim(),
      cmb_number: Number(cmbNumber),
      intended_company_id: intended ? Number(intended) : null,
      notes: notes.trim() || null,
    }),
    {
      onSuccess: (data) => {
        setIssuedToken(data.token);
        qc.invalidateQueries(['manufacturer-stock']);
        toast.success(`Caixa CMB ${data.stock.cmb_number} criada — token gerado.`);
      },
      onError: (e: any) => {
        toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`);
      },
    },
  );

  const reset = () => {
    setBluetooth(''); setCmbNumber(''); setIntended(''); setNotes('');
    setIssuedToken(null); setCopied(false);
  };

  const close = () => { reset(); onClose(); };

  const copyToken = async () => {
    if (!issuedToken) return;
    try {
      await navigator.clipboard.writeText(issuedToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar — selecione o texto manualmente.');
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '60px 20px', zIndex: 1000, overflowY: 'auto',
    }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <PageCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionLabel>{issuedToken ? 'Token gerado' : 'Nova caixa em estoque'}</SectionLabel>
            <button type='button' onClick={close} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} color={TOKENS.color.muted} />
            </button>
          </div>

          {issuedToken ? (
            // === SUCCESS VIEW — token visible exactly once ===
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, color: TOKENS.color.text, lineHeight: 1.5 }}>
                Caixa criada. <strong>Copie o token abaixo agora</strong> e cole no firmware.
                Você pode acessá-lo de novo pelo botão "Ver token" na lista, mas só enquanto
                a caixa estiver pendente.
              </div>
              <div style={{
                background: TOKENS.color.surface,
                border: `1px solid ${TOKENS.color.border}`,
                borderRadius: TOKENS.radius.chip,
                padding: '10px 12px',
                fontFamily: 'monospace',
                fontSize: 11,
                wordBreak: 'break-all',
                maxHeight: 140,
                overflowY: 'auto',
                color: TOKENS.color.text,
              }}>
                {issuedToken}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type='button'
                  onClick={copyToken}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: copied ? TOKENS.color.severity.low.bg : TOKENS.color.action,
                    color: copied ? TOKENS.color.severity.low.text : '#fff',
                    border: 'none', padding: '8px 14px', fontSize: 13, fontWeight: 700,
                    borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                  }}
                >
                  {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar token</>}
                </button>
                <button
                  type='button'
                  onClick={() => { setIssuedToken(null); reset(); }}
                  style={{
                    background: TOKENS.color.card,
                    color: TOKENS.color.text,
                    border: `1px solid ${TOKENS.color.border}`,
                    padding: '8px 14px', fontSize: 13, fontWeight: 600,
                    borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                  }}
                >
                  Criar outra caixa
                </button>
                <button
                  type='button'
                  onClick={close}
                  style={{
                    background: TOKENS.color.card,
                    color: TOKENS.color.muted,
                    border: `1px solid ${TOKENS.color.border}`,
                    padding: '8px 14px', fontSize: 13, fontWeight: 600,
                    borderRadius: TOKENS.radius.chip, cursor: 'pointer', marginLeft: 'auto',
                  }}
                >Fechar</button>
              </div>
            </div>
          ) : (
            // === FORM VIEW ===
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label='Bluetooth (obrigatório)'>
                <input
                  type='text' value={bluetooth}
                  placeholder='AA:BB:CC:DD:EE:FF'
                  onChange={(e) => setBluetooth(e.target.value)}
                  style={inputStyle}
                />
              </Field>
              <Field label='CMB number (obrigatório)'>
                <input
                  type='number' value={cmbNumber}
                  onChange={(e) => setCmbNumber(e.target.value)}
                  style={inputStyle}
                />
              </Field>
              <Field label='Empresa destino (opcional — só documentação)'>
                <input
                  type='number' value={intended}
                  placeholder='ID da empresa que vai receber'
                  onChange={(e) => setIntended(e.target.value)}
                  style={inputStyle}
                />
              </Field>
              <Field label='Notas (opcional)'>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button type='button' onClick={close} style={ghostBtn}>Cancelar</button>
                <button
                  type='button'
                  disabled={!bluetooth.trim() || !cmbNumber || create.isLoading}
                  onClick={() => create.mutate()}
                  style={{
                    ...primaryBtn,
                    opacity: (!bluetooth.trim() || !cmbNumber || create.isLoading) ? 0.5 : 1,
                    cursor: (!bluetooth.trim() || !cmbNumber || create.isLoading) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {create.isLoading ? 'Criando…' : 'Criar caixa'}
                </button>
              </div>
            </div>
          )}
        </PageCard>
      </div>
    </div>
  );
};

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: TOKENS.color.muted }}>
      {label}
    </span>
    {children}
  </label>
);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
  background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
};
const primaryBtn: React.CSSProperties = {
  background: TOKENS.color.action, color: '#fff', border: 'none',
  padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: TOKENS.radius.chip,
};
const ghostBtn: React.CSSProperties = {
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '8px 14px', fontSize: 13, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
